import { Button } from '@consta/uikit/Button'
import { Modal } from '@consta/uikit/Modal'
import { Text } from '@consta/uikit/Text'
import { FC, useState, useRef, useEffect } from 'react'
import { IconClose } from '@consta/icons/IconClose'
import { IconDocBlank } from '@consta/icons/IconDocBlank'
import { Combobox } from '@consta/uikit/Combobox'
import { 
    useUpdateChromatogramMutation, 
    usePostChromatogramMutation 
} from '../../../services/chromatogramService'
import { useLazyGetWellByProjectQuery } from '../../../services/wellService'

type AddChromaProps = {
    isOpen: boolean,
    setIsOpen: (isOpen: boolean) => void
    handleCloseModal: () => void;
    projectUuid?: string;
    onChromatogramUpdated?: () => void | any;
    chromatogramUuid?: string;
    isEditMode?: boolean;
}

type FileWithPreview = File & {
    preview?: string;
}

type WellItem = {
    id: string;
    label: string;
    uuid: string;
    number: number;
    field?: string;
}

const AddChroma: FC<AddChromaProps> = ({ 
    isOpen, 
    setIsOpen, 
    handleCloseModal, 
    projectUuid, 
    onChromatogramUpdated,
    chromatogramUuid,
    isEditMode = false
}) => {
    const [updateChromatogram, { isLoading: isUpdating }] = useUpdateChromatogramMutation();
    const [postChromatogram, { isLoading: isCreating }] = usePostChromatogramMutation();
    const [triggerGetWellByProject, { data: wellsData, isLoading: isLoadingWells }] = useLazyGetWellByProjectQuery();
    
    const [selectedFile, setSelectedFile] = useState<FileWithPreview | null>(null);
    const [selectedWell, setSelectedWell] = useState<WellItem | null>(null);
    const [wells, setWells] = useState<WellItem[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const isLoading = isUpdating || isCreating;

    // Загружаем список скважин проекта при открытии модалки
    useEffect(() => {
        if (isOpen && projectUuid) {
            //('🔄 Загрузка скважин проекта:', projectUuid);
            triggerGetWellByProject({ project_uuid: projectUuid })
                .unwrap()
                .then((data) => {
                    if (data && data.results) {
                        const wellsList: WellItem[] = data.results.map((well: any) => ({
                            id: well.uuid,
                            label: `Скважина ${well.number}`,
                            uuid: well.uuid,
                            number: well.number,
                            field: well.area_name || 'Не указано'
                        }));
                        setWells(wellsList);
                        //('✅ Скважины загружены:', wellsList);
                    }
                })
                .catch((err) => {
                    console.error('❌ Ошибка загрузки скважин:', err);
                    setError('Не удалось загрузить список скважин');
                });
        }
    }, [isOpen, projectUuid, triggerGetWellByProject]);

    // Сбрасываем состояние при открытии/закрытии модалки
    useEffect(() => {
        if (!isOpen) {
            // При закрытии модалки сбрасываем все состояния
            if (selectedFile?.preview) {
                URL.revokeObjectURL(selectedFile.preview);
            }
            setSelectedFile(null);
            setSelectedWell(null);
            setError('');
        }
    }, [isOpen]);

    const handleFileSelect = (files: File[] | null) => {
        if (files && files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                const fileWithPreview = Object.assign(file, {
                    preview: URL.createObjectURL(file)
                });
                setSelectedFile(fileWithPreview);
                setError('');
            } else {
                setError('Пожалуйста, выберите файл изображения (JPG, PNG, GIF, BMP)');
            }
        }
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
        
        const files = Array.from(event.dataTransfer.files);
        handleFileSelect(files);
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setIsDragOver(false);
    };

    const handleRemoveFile = () => {
        if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
        }
        setSelectedFile(null);
        setError('');
        
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSave = async () => {
        if (!selectedWell) {
            setError('Пожалуйста, выберите скважину');
            return;
        }

        if (!selectedFile) {
            setError('Пожалуйста, выберите файл хроматограммы для загрузки');
            return;
        }

        if (!projectUuid) {
            setError('Не указан проект');
            return;
        }

        setError('');

        try {
            // Создаем FormData для отправки файла
            const formData = new FormData();
            formData.append('name', `Хроматограмма скважины ${selectedWell.number}`);
            formData.append('image', selectedFile);
            formData.append('well', selectedWell.uuid);
            formData.append('project', projectUuid);

            if (isEditMode && chromatogramUuid) {
                // Режим обновления существующей хроматограммы
                //('🔄 Режим обновления хроматограммы');
                formData.append('uuid', chromatogramUuid);

                

                await updateChromatogram(formData).unwrap();
                //('✅ Хроматограмма успешно обновлена');
            } else {
                // Режим создания новой хроматограммы
                //('🆕 Режим создания хроматограммы');

               

                await postChromatogram(formData).unwrap();
                //('✅ Хроматограмма успешно создана');
            }

            // Очищаем preview URL
            if (selectedFile.preview) {
                URL.revokeObjectURL(selectedFile.preview);
            }

            // Закрываем модалку только после успешного запроса
            handleCloseModal();
            
            // Вызываем колбэк для обновления родительского компонента
            // после закрытия модалки, чтобы избежать повторного открытия
            // setTimeout(() => {
            //     onChromatogramUpdated();
            // }, 100);
            
        } catch (err) {
            console.error('❌ Ошибка сохранения хроматограммы:', err);
            setError(`Не удалось ${isEditMode ? 'обновить' : 'создать'} хроматограмму. Пожалуйста, попробуйте еще раз.`);
        }
    };

    const handleClose = () => {
        // Очищаем состояния при закрытии
        if (selectedFile?.preview) {
            URL.revokeObjectURL(selectedFile.preview);
        }
        setSelectedFile(null);
        setSelectedWell(null);
        setError('');
        handleCloseModal();
    };

    const handleWellChange = (value: WellItem | null) => {
        setSelectedWell(value);
        setError('');
    };

    const getModalTitle = () => {
        return isEditMode ? 'Обновить хроматограмму' : 'Создать хроматограмму';
    };

    const getButtonLabel = () => {
        if (isLoading) return "Сохранение...";
        return isEditMode ? "Обновить хроматограмму" : "Создать хроматограмму";
    };

    return (
        <div>
            <Modal
                isOpen={isOpen}
                hasOverlay
                onClickOutside={handleClose}
                onEsc={handleClose}
                style={{ width: '600px' }}
            >
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text size='xl' weight='bold'>{getModalTitle()}</Text>
                        <Button 
                            size='s' 
                            iconLeft={IconClose} 
                            view='clear' 
                            onClick={handleClose}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Информация о проекте */}
                    {projectUuid && (
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: 'var(--color-bg-secondary)', 
                            borderRadius: '8px',
                            border: '1px solid var(--color-bg-border)'
                        }}>
                            <Text size="s" weight="semibold">Проект:</Text>
                            <Text size="s" view="secondary">UUID: {projectUuid}</Text>
                        </div>
                    )}

                    {/* Информация о хроматограмме (только в режиме редактирования) */}
                    {isEditMode && chromatogramUuid && (
                        <div style={{ 
                            padding: '12px', 
                            backgroundColor: 'var(--color-bg-secondary)', 
                            borderRadius: '8px',
                            border: '1px solid var(--color-bg-border)'
                        }}>
                            <Text size="s" weight="semibold">Хроматограмма для обновления:</Text>
                            <Text size="s" view="secondary">UUID: {chromatogramUuid}</Text>
                        </div>
                    )}

                    {error && (
                        <Text view="alert" size="s">
                            {error}
                        </Text>
                    )}

                    {/* Выбор скважины */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <Text size='m' weight='semibold'>Выберите скважину</Text>
                        <Combobox
                            items={wells}
                            value={selectedWell}
                            onChange={handleWellChange}
                            placeholder={isLoadingWells ? "Загрузка скважин..." : "Выберите скважину из списка"}
                            getItemLabel={(item) => item.label}
                            getItemKey={(item) => item.id}
                            style={{ width: '100%' }}
                            disabled={isLoadingWells || isLoading}
                        />
                        {selectedWell && (
                            <Text size="xs" view="secondary">
                                UUID скважины: {selectedWell.uuid}
                            </Text>
                        )}
                        {isLoadingWells && (
                            <Text size="xs" view="secondary">
                                Загрузка списка скважин...
                            </Text>
                        )}
                        {!isLoadingWells && wells.length === 0 && (
                            <Text size="xs" view="alert">
                                В проекте нет доступных скважин
                            </Text>
                        )}
                    </div>

                    {/* Область для загрузки файла хроматограммы */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Text size='m' weight='semibold'>Загрузите изображение хроматограммы</Text>
                        
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                    handleFileSelect(Array.from(e.target.files));
                                }
                            }}
                            disabled={isLoading}
                        />

                        <div
                            style={{
                                border: `2px dashed ${isDragOver ? 'var(--color-control-bg-primary)' : 'var(--color-control-bg-border-default)'}`,
                                borderRadius: '8px',
                                padding: '40px 20px',
                                textAlign: 'center',
                                backgroundColor: isDragOver ? 'var(--color-control-bg-primary-hover)' : 'var(--color-bg-secondary)',
                                cursor: (selectedWell && !isLoading) ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease',
                                minHeight: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: (selectedWell && !isLoading) ? 1 : 0.6
                            }}
                            onDrop={selectedWell && !isLoading ? handleDrop : undefined}
                            onDragOver={selectedWell && !isLoading ? handleDragOver : undefined}
                            onDragLeave={selectedWell && !isLoading ? handleDragLeave : undefined}
                            onClick={selectedWell && !isLoading ? () => fileInputRef.current?.click() : undefined}
                        >
                            {!selectedWell ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    {/*@ts-ignore */}
                                    <IconDocBlank size="m" view="secondary" />
                                    <Text size="s" view="secondary" weight="semibold">
                                        Сначала выберите скважину
                                    </Text>
                                </div>
                            ) : isLoading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    <Text size="s" view="secondary">
                                        Идет сохранение...
                                    </Text>
                                </div>
                            ) : selectedFile ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    {selectedFile.preview && (
                                        <div style={{ 
                                            marginTop: '12px', 
                                            maxWidth: '250px', 
                                            maxHeight: '180px', 
                                            overflow: 'hidden', 
                                            borderRadius: '4px',
                                            border: '1px solid var(--color-control-bg-border-default)',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            <img 
                                                src={selectedFile.preview} 
                                                alt="Preview хроматограммы" 
                                                style={{ 
                                                    width: '100%', 
                                                    height: 'auto', 
                                                    objectFit: 'contain',
                                                    display: 'block'
                                                }}
                                            />
                                        </div>
                                    )}
                                    <Button
                                        size="xs"
                                        view="ghost"
                                        label="Удалить файл"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveFile();
                                        }}
                                        disabled={isLoading}
                                    />
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                                    {/*@ts-ignore */}
                                    <IconDocBlank size="m" view="secondary" />
                                    <div>
                                        <Text size="s" view="primary" weight="semibold">
                                            Перетащите изображение хроматограммы или нажмите для выбора
                                        </Text>
                                        <Text size="xs" view="secondary" style={{ marginTop: '4px' }}>
                                            Поддерживаемые форматы: JPG, PNG, GIF, BMP, WEBP, TIFF
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'end', gap: '12px' }}>
                        <Button
                            size="s"
                            view="secondary"
                            label="Отмена"
                            onClick={handleClose}
                            disabled={isLoading}
                        />
                        <Button
                            size="s"
                            view="primary"
                            label={getButtonLabel()}
                            onClick={handleSave}
                            disabled={!selectedWell || !selectedFile || isLoading}
                            loading={isLoading}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default AddChroma